import { useEffect, useState } from 'react';
import { PatientState } from '../../../services/redux/partner/patient';
import {
  PatientShippingFormGroup,
  useFormFieldEffect,
  FormField,
  validateRequired,
  validateLength,
} from '../..';

export interface PatientShippingFormFields {
  shippingFirstName: string | null;
  shippingLastName: string | null;
  shippingCompany: string | null;
  shippingLine1: string | null;
  shippingLine2: string | null;
  shippingCity: string | null;
  shippingState: string | null;
  shippingPostalCode: string | null;
  shippingCountry: string | null;
  shippingPhone: string | null;
  matchAddress: boolean | null;
  billingFirstName: string | null;
  billingLastName: string | null;
  billingCompany: string | null;
  billingLine1: string | null;
  billingLine2: string | null;
  billingCity: string | null;
  billingState: string | null;
  billingPostalCode: string | null;
  billingCountry: string | null;
  billingPhone: string | null;
}

export interface PatientShippingFormValidation {
  isValid: boolean;
  isClean: boolean;
  isSubmitted: boolean;
  isValidRegistration: boolean;
  isValidShipping: boolean;
  isValidBilling: boolean;
}

export const usePatientShippingFormEffect = (
  memberState: PatientState
): PatientShippingFormGroup => {
  const {
    formData: {
      address: { billingAddress, shippingAddress },
    },
    pageState: { validRegistration, validShipping, validBilling },
  } = memberState;

  const [currentPatientForm, setPatientForm] =
    useState<PatientShippingFormValidation>({
      isValid: validShipping && validBilling,
      isClean: true,
      isSubmitted: false,
      isValidRegistration: validRegistration,
      isValidShipping: validShipping,
      isValidBilling: validBilling,
    });

  const shippingFirstName = useFormFieldEffect(
    'shippingFirstName',
    'text',
    [validateRequired, validateLength],
    { required: true, minLength: 3, maxLength: 24 },
    shippingAddress?.firstName || ''
  );

  const shippingLastName = useFormFieldEffect(
    'shippingLastName',
    'text',
    [validateRequired, validateLength],
    { required: true, minLength: 3, maxLength: 24 },
    shippingAddress?.lastName || ''
  );

  const shippingCompany = useFormFieldEffect(
    'shippingCompany',
    'text',
    [validateLength],
    { minLength: 3, maxLength: 24 },
    shippingAddress?.company || ''
  );

  const shippingLine1 = useFormFieldEffect(
    'shippingLine1',
    'text',
    [validateRequired, validateLength],
    { required: true, minLength: 3, maxLength: 24 },
    shippingAddress?.line1 || ''
  );

  const shippingLine2 = useFormFieldEffect(
    'shippingLine2',
    'text',
    [validateLength],
    { minLength: 3, maxLength: 24 },
    shippingAddress?.line2 || ''
  );

  const shippingCity = useFormFieldEffect(
    'shippingCity',
    'text',
    [validateRequired, validateLength],
    { required: true, minLength: 3, maxLength: 24 },
    shippingAddress?.city || ''
  );

  const shippingState = useFormFieldEffect(
    'shippingState',
    'select',
    [validateRequired],
    {
      required: true,
    },
    shippingAddress?.state || ''
  );

  const shippingPostalCode = useFormFieldEffect(
    'shippingPostalCode',
    'text',
    [validateRequired, validateLength],
    { required: true, minLength: 4, maxLength: 8 },
    shippingAddress?.postalCode || ''
  );

  const shippingCountry = useFormFieldEffect(
    'shippingCountry',
    'select',
    [validateRequired],
    {
      required: true,
    },
    shippingAddress?.country || ''
  );

  const shippingPhone = useFormFieldEffect(
    'shippingPhone',
    'text',
    [validateRequired],
    {
      required: true,
    },
    shippingAddress?.phone || ''
  );

  const matchAddress = useFormFieldEffect(
    'matchAddress',
    'radiobox',
    [],
    {},
    memberState.formData.address.matchAddress
  );

  const billingFirstName = useFormFieldEffect(
    'billingFirstName',
    'text',
    [validateRequired, validateLength],
    { required: true, minLength: 3, maxLength: 24 },
    billingAddress?.firstName || ''
  );

  const billingLastName = useFormFieldEffect(
    'billingLastName',
    'text',
    [validateRequired, validateLength],
    { required: true, minLength: 3, maxLength: 24 },
    billingAddress?.lastName || ''
  );

  const billingCompany = useFormFieldEffect(
    'billingCompany',
    'text',
    [validateLength],
    { minLength: 3, maxLength: 24 },
    billingAddress?.company || ''
  );

  const billingLine1 = useFormFieldEffect(
    'billingLine1',
    'text',
    [validateRequired, validateLength],
    { required: true, minLength: 3, maxLength: 24 },
    billingAddress?.line1 || ''
  );

  const billingLine2 = useFormFieldEffect(
    'billingLine2',
    'text',
    [validateLength],
    { minLength: 3, maxLength: 24 },
    billingAddress?.line2 || ''
  );

  const billingCity = useFormFieldEffect(
    'billingCity',
    'text',
    [validateRequired, validateLength],
    { required: true, minLength: 3, maxLength: 24 },
    billingAddress?.city || ''
  );

  const billingState = useFormFieldEffect(
    'billingState',
    'select',
    [validateRequired],
    {
      required: true,
    },
    billingAddress?.state || ''
  );

  const billingPostalCode = useFormFieldEffect(
    'billingPostalCode',
    'text',
    [validateRequired, validateLength],
    { required: true, minLength: 4, maxLength: 8 },
    billingAddress?.postalCode || ''
  );

  const billingCountry = useFormFieldEffect(
    'billingCountry',
    'select',
    [validateRequired],
    {
      required: true,
    },
    billingAddress?.country || ''
  );

  const billingPhone = useFormFieldEffect(
    'billingPhone',
    'text',
    [validateRequired],
    {
      required: true,
    },
    billingAddress?.phone || ''
  );

  const members = {
    shippingFirstName,
    shippingLastName,
    shippingCompany,
    shippingLine1,
    shippingLine2,
    shippingCity,
    shippingState,
    shippingPostalCode,
    shippingCountry,
    shippingPhone,
    matchAddress,
    billingFirstName,
    billingLastName,
    billingCompany,
    billingLine1,
    billingLine2,
    billingCity,
    billingState,
    billingPostalCode,
    billingCountry,
    billingPhone,
  };

  useEffect(() => {
    let valid = true;
    let clean = true;

    const formFields = members as any as { [key: string]: FormField };

    for (const i in formFields) {
      if (!formFields[i].isValid) {
        valid = false;
      }

      if (!formFields[i].isClean) {
        clean = false;
      }
    }

    const validateShippingResult = !(
      !formFields.shippingFirstName.value ||
      !formFields.shippingFirstName.isValid ||
      !formFields.shippingLastName.value ||
      !formFields.shippingLastName.isValid ||
      !formFields.shippingLine1.value ||
      !formFields.shippingLine1.isValid ||
      !formFields.shippingCity.value ||
      !formFields.shippingCity.isValid ||
      !formFields.shippingState.value ||
      !formFields.shippingState.isValid ||
      !formFields.shippingCountry.value ||
      !formFields.shippingCountry.isValid ||
      !formFields.shippingPostalCode.value ||
      !formFields.shippingPostalCode.isValid ||
      !formFields.shippingPhone.value ||
      !formFields.shippingPhone.isValid
    );

    const validateBillingResult = !(
      !formFields.billingFirstName.value ||
      !formFields.billingFirstName.isValid ||
      !formFields.billingLastName.value ||
      !formFields.billingLastName.isValid ||
      !formFields.billingLine1.value ||
      !formFields.billingLine1.isValid ||
      !formFields.billingCity.value ||
      !formFields.billingCity.isValid ||
      !formFields.billingState.value ||
      !formFields.billingState.isValid ||
      !formFields.billingCountry.value ||
      !formFields.billingCountry.isValid ||
      !formFields.billingPostalCode.value ||
      !formFields.billingPostalCode.isValid ||
      !formFields.billingPhone.value ||
      !formFields.billingPhone.isValid
    );

    const updatedPatientForm: PatientShippingFormValidation = {
      isValid: valid,
      isClean: clean,
      isSubmitted: currentPatientForm.isSubmitted,
      isValidRegistration: currentPatientForm.isValidRegistration,
      isValidShipping: validateShippingResult,
      isValidBilling: validateBillingResult,
    };

    const updatedPatientStateString = JSON.stringify(updatedPatientForm);
    const currentPatientFormString = JSON.stringify(currentPatientForm);

    if (updatedPatientStateString !== currentPatientFormString) {
      setPatientForm(updatedPatientForm);
    }
  }, [members, currentPatientForm, setPatientForm]);

  const getFieldValues = (): PatientShippingFormFields => {
    return {
      shippingFirstName: (members.shippingFirstName.value as string) || null,
      shippingLastName: (members.shippingLastName.value as string) || null,
      shippingCompany: (members.shippingCompany.value as string) || null,
      shippingLine1: (members.shippingLine1.value as string) || null,
      shippingLine2: (members.shippingLine2.value as string) || null,
      shippingCity: (members.shippingCity.value as string) || null,
      shippingState: (members.shippingState.value as string) || null,
      shippingPostalCode: (members.shippingPostalCode.value as string) || null,
      shippingCountry: (members.shippingCountry.value as string) || null,
      shippingPhone: (members.shippingPhone.value as string) || null,
      matchAddress: (members.matchAddress.value as boolean) || null,
      billingFirstName: (members.billingFirstName.value as string) || null,
      billingLastName: (members.billingLastName.value as string) || null,
      billingCompany: (members.billingCompany.value as string) || null,
      billingLine1: (members.billingLine1.value as string) || null,
      billingLine2: (members.billingLine2.value as string) || null,
      billingCity: (members.billingCity.value as string) || null,
      billingState: (members.billingState.value as string) || null,
      billingPostalCode: (members.billingPostalCode.value as string) || null,
      billingCountry: (members.billingCountry.value as string) || null,
      billingPhone: (members.billingPhone.value as string) || null,
    };
  };

  const resetFieldValues = (clean = false) => {
    members.shippingFirstName.doReset(clean);
    members.shippingLastName.doReset(clean);
    members.shippingCompany.doReset(clean);
    members.shippingLine1.doReset(clean);
    members.shippingLine2.doReset(clean);
    members.shippingCity.doReset(clean);
    members.shippingState.doReset(clean);
    members.shippingPostalCode.doReset(clean);
    members.shippingCountry.doReset(clean);
    members.shippingPhone.doReset(clean);
    members.matchAddress.doReset(clean);
    members.billingFirstName.doReset(clean);
    members.billingLastName.doReset(clean);
    members.billingCompany.doReset(clean);
    members.billingLine1.doReset(clean);
    members.billingLine2.doReset(clean);
    members.billingCity.doReset(clean);
    members.billingState.doReset(clean);
    members.billingPostalCode.doReset(clean);
    members.billingCountry.doReset(clean);
    members.billingPhone.doReset(clean);
  };

  return {
    isValid: currentPatientForm.isValid,
    isClean: currentPatientForm.isClean,
    isSubmitted: currentPatientForm.isSubmitted,
    isValidShipping: currentPatientForm.isValidShipping,
    isValidBilling: currentPatientForm.isValidBilling,
    getFieldValues,
    resetFieldValues,
    members,
  };
};
